/**
 * React Query hooks for /api/v1/ndt-inspection/* (fieldiq-api#1134, batch 1).
 *
 * EVERY HOOK RETURNS THE META. An NDT read is `{ data, meta }`, and a screen
 * cannot label a length, an activity or a dose without `meta.units`, or
 * default a date without `meta.today` (the tenant's day, not the browser's).
 *
 * EVERY KEY STARTS ['ndtInspection'] and every write invalidates that one
 * prefix. A settings change moves the warn windows, which moves every status;
 * a calibration moves the register and the detail; a custody event moves the
 * source list, the detail and its log. One prefix is the honest answer to
 * "what did this write change".
 *
 * EVERY HOOK SPREADS LOAD_FAILURE_QUERY_OPTIONS. Read a failure with
 * `queryLoadFailure(query)` (lib/load-failure.ts).
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { putToPresignedUrl } from '../api/upload';
import { unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { isNdtDayInput } from '../lib/ndt-inspection';
import type {
  NdtCalibration,
  NdtCalibrationCreate,
  NdtCatalog,
  NdtCertWrite,
  NdtDecay,
  NdtDose,
  NdtDoseWrite,
  NdtInstrument,
  NdtInstrumentDetail,
  NdtMeta,
  NdtQualWrite,
  NdtResult,
  NdtSettings,
  NdtSettingsUpdate,
  NdtSource,
  NdtSourceDetail,
  NdtSourceLog,
  NdtSourceLogCreate,
  NdtSourceWrite,
  NdtSpecsWrite,
  NdtTechnicianCert,
  NdtWelder,
  NdtWelderQual,
  NdtWelderWrite,
  NdtCrew,
  NdtCrewWrite,
  NdtExam,
  NdtExamWrite,
  NdtFieldExam,
  NdtFieldJob,
  NdtFieldJobDetail,
  NdtFieldMeta,
  NdtFieldWeld,
  NdtJob,
  NdtJobDetail,
  NdtJobWrite,
  NdtRepairRates,
  NdtShotWarning,
  NdtWeld,
  NdtWeldDetail,
  NdtWeldWrite,
  NdtReport,
  NdtReportCreate,
  NdtReportUpdate,
  NdtReportWarning,
} from '../types/ndt-inspection';

/** No zone at all — absent meta prints no time, never a guessed zone. */
const NO_ZONE: string | null = null;

const EMPTY_META: NdtMeta = {
  unitSystem: null,
  unitSystemSource: null,
  units: null,
  currency: null,
  timeZone: NO_ZONE,
  today: null,
  calWarnDays: null,
  expiryWarnDays: null,
};

/**
 * Split an NDT body into payload and meta. `data: null` stays null — never the
 * envelope (the `res.data?.data || res.data` bug in CLAUDE.md). A missing meta
 * is all-null, so a screen prints `—` and no unit rather than guessing one.
 */
export function unwrapNdt<T>(body: unknown): NdtResult<T | null> {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    return { data: null, meta: EMPTY_META };
  }
  const b = body as { data?: unknown; meta?: Partial<NdtMeta> };
  const data = 'data' in b && b.data !== undefined && b.data !== null ? (b.data as T) : null;
  return { data, meta: { ...EMPTY_META, ...(b.meta ?? {}) } };
}

/** A list read: a non-array payload becomes `[]` rather than reaching `.map`. */
export function unwrapNdtList<T>(body: unknown): NdtResult<T[]> {
  const { data, meta } = unwrapNdt<unknown>(body);
  return { data: Array.isArray(data) ? (data as T[]) : [], meta };
}

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = v;
  return Object.keys(out).length ? out : undefined;
};

export const ndtKeys = {
  all: ['ndtInspection'] as const,
  catalog: () => ['ndtInspection', 'catalog'] as const,
  settings: () => ['ndtInspection', 'settings'] as const,
  certs: (p?: Params) => ['ndtInspection', 'certs', clean(p) ?? {}] as const,
  cert: (id: string) => ['ndtInspection', 'cert', id] as const,
  welders: (p?: Params) => ['ndtInspection', 'welders', clean(p) ?? {}] as const,
  welder: (id: string) => ['ndtInspection', 'welder', id] as const,
  instruments: (p?: Params) => ['ndtInspection', 'instruments', clean(p) ?? {}] as const,
  instrument: (id: string) => ['ndtInspection', 'instrument', id] as const,
  sources: (p?: Params) => ['ndtInspection', 'sources', clean(p) ?? {}] as const,
  source: (id: string) => ['ndtInspection', 'source', id] as const,
  decay: (id: string, on: string) => ['ndtInspection', 'decay', id, on] as const,
  doses: (p?: Params) => ['ndtInspection', 'doses', clean(p) ?? {}] as const,
  people: () => ['ndtInspection', 'people'] as const,
  equipment: () => ['ndtInspection', 'equipment'] as const,
  jobs: (p?: Params) => ['ndtInspection', 'jobs', clean(p) ?? {}] as const,
  job: (id: string) => ['ndtInspection', 'job', id] as const,
  jobWelds: (id: string) => ['ndtInspection', 'jobWelds', id] as const,
  welds: (p?: Params) => ['ndtInspection', 'welds', clean(p) ?? {}] as const,
  weld: (id: string) => ['ndtInspection', 'weld', id] as const,
  repairRates: (p?: Params) => ['ndtInspection', 'repairRates', clean(p) ?? {}] as const,
  customers: () => ['ndtInspection', 'customers'] as const,
  fieldJobs: () => ['ndtInspection', 'fieldJobs'] as const,
  fieldJob: (id: string) => ['ndtInspection', 'fieldJob', id] as const,
  reports: (p?: Params) => ['ndtInspection', 'reports', clean(p) ?? {}] as const,
  report: (id: string) => ['ndtInspection', 'report', id] as const,
};

export function invalidateNdt(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: ndtKeys.all });
}

export function useNdtCatalog() {
  return useQuery({
    queryKey: ndtKeys.catalog(),
    queryFn: async () => unwrapNdt<NdtCatalog>((await api.ndtInspection.catalog()).data),
    staleTime: 5 * 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtSettings() {
  return useQuery({
    queryKey: ndtKeys.settings(),
    queryFn: async () => unwrapNdt<NdtSettings>((await api.ndtInspection.settings.get()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtCerts(params?: { userId?: string; method?: string; status?: string }) {
  return useQuery({
    queryKey: ndtKeys.certs(params),
    queryFn: async () => unwrapNdtList<NdtTechnicianCert>((await api.ndtInspection.certs.list(clean(params))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtCert(id: string | undefined) {
  return useQuery({
    queryKey: ndtKeys.cert(id ?? ''),
    queryFn: async () => unwrapNdt<NdtTechnicianCert>((await api.ndtInspection.certs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtWelders(params?: { q?: string }) {
  return useQuery({
    queryKey: ndtKeys.welders(params),
    queryFn: async () => unwrapNdtList<NdtWelder>((await api.ndtInspection.welders.list(clean(params))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtWelder(id: string | undefined) {
  return useQuery({
    queryKey: ndtKeys.welder(id ?? ''),
    queryFn: async () => unwrapNdt<NdtWelder>((await api.ndtInspection.welders.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtInstruments(params?: { kind?: string; status?: string; q?: string }) {
  return useQuery({
    queryKey: ndtKeys.instruments(params),
    queryFn: async () => unwrapNdtList<NdtInstrument>((await api.ndtInspection.instruments.list(clean(params))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtInstrument(id: string | undefined) {
  return useQuery({
    queryKey: ndtKeys.instrument(id ?? ''),
    queryFn: async () => unwrapNdt<NdtInstrumentDetail>((await api.ndtInspection.instruments.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtSources(params?: { status?: string; isotope?: string }) {
  return useQuery({
    queryKey: ndtKeys.sources(params),
    queryFn: async () => unwrapNdtList<NdtSource>((await api.ndtInspection.sources.list(clean(params))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtSource(id: string | undefined) {
  return useQuery({
    queryKey: ndtKeys.source(id ?? ''),
    queryFn: async () => unwrapNdt<NdtSourceDetail>((await api.ndtInspection.sources.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Activity on a chosen day — the API's decay, never computed here. Runs only once a day is picked. */
export function useNdtDecay(id: string | undefined, on: string) {
  return useQuery({
    queryKey: ndtKeys.decay(id ?? '', on),
    queryFn: async () => unwrapNdt<NdtDecay>((await api.ndtInspection.sources.decay(id as string, on)).data),
    enabled: !!id && isNdtDayInput(on),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtDoses(params?: { userId?: string }) {
  return useQuery({
    queryKey: ndtKeys.doses(params),
    queryFn: async () => unwrapNdtList<NdtDose>((await api.ndtInspection.dosimetry.list(clean(params))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export interface NdtPerson {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  role?: string;
}
export const ndtPersonName = (u: NdtPerson) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || '—';

/** The tenant's people, customers excluded — for the technician, holder and dose pickers. */
export function useNdtPeople(enabled = true) {
  return useQuery({
    queryKey: ndtKeys.people(),
    queryFn: async () => unwrapArray<NdtPerson>((await api.users.list()).data).filter((u) => u.role !== 'customer'),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export interface NdtEquipmentOption {
  id: string;
  name: string | null;
  serialNumber: string | null;
  type: string | null;
}

/** The company's equipment, for "attach an existing unit" and a source's exposure device. */
export function useNdtEquipmentOptions(enabled = true) {
  return useQuery({
    queryKey: ndtKeys.equipment(),
    queryFn: async () => unwrapArray<NdtEquipmentOption>((await api.equipment.list()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useNdtWrite<V, R>(call: (vars: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => unwrapNdt<R>((await call(vars)).data),
    onSuccess: () => invalidateNdt(qc),
  });
}

export const useUpdateNdtSettings = () =>
  useNdtWrite<NdtSettingsUpdate, NdtSettings>((body) => api.ndtInspection.settings.update(body));

export const useCreateNdtCert = () =>
  useNdtWrite<NdtCertWrite, NdtTechnicianCert>((body) => api.ndtInspection.certs.create(body));
export const useUpdateNdtCert = () =>
  useNdtWrite<{ id: string; body: NdtCertWrite }, NdtTechnicianCert>(({ id, body }) => api.ndtInspection.certs.update(id, body));
export const useDeleteNdtCert = () => useNdtWrite<string, unknown>((id) => api.ndtInspection.certs.remove(id));

export const useCreateNdtWelder = () =>
  useNdtWrite<NdtWelderWrite, NdtWelder>((body) => api.ndtInspection.welders.create(body));
export const useUpdateNdtWelder = () =>
  useNdtWrite<{ id: string; body: NdtWelderWrite }, NdtWelder>(({ id, body }) => api.ndtInspection.welders.update(id, body));
export const useDeleteNdtWelder = () => useNdtWrite<string, unknown>((id) => api.ndtInspection.welders.remove(id));
export const useAddNdtQual = () =>
  useNdtWrite<{ welderId: string; body: NdtQualWrite }, NdtWelderQual>(({ welderId, body }) =>
    api.ndtInspection.welders.addQual(welderId, body),
  );
export const useUpdateNdtQual = () =>
  useNdtWrite<{ id: string; body: NdtQualWrite }, NdtWelderQual>(({ id, body }) => api.ndtInspection.welders.updateQual(id, body));
export const useDeleteNdtQual = () => useNdtWrite<string, unknown>((id) => api.ndtInspection.welders.removeQual(id));

export const useUpdateNdtSpecs = () =>
  useNdtWrite<{ id: string; body: NdtSpecsWrite }, NdtInstrument>(({ id, body }) => api.ndtInspection.instruments.update(id, body));
export const useDetachNdtInstrument = () => useNdtWrite<string, unknown>((id) => api.ndtInspection.instruments.detach(id));
export const useRecordNdtCalibration = () =>
  useNdtWrite<{ id: string; body: NdtCalibrationCreate }, NdtCalibration>(({ id, body }) =>
    api.ndtInspection.instruments.recordCalibration(id, body),
  );

export const useCreateNdtSource = () =>
  useNdtWrite<NdtSourceWrite, NdtSource>((body) => api.ndtInspection.sources.create(body));
export const useUpdateNdtSource = () =>
  useNdtWrite<{ id: string; body: NdtSourceWrite }, NdtSource>(({ id, body }) => api.ndtInspection.sources.update(id, body));
export const useDeleteNdtSource = () => useNdtWrite<string, unknown>((id) => api.ndtInspection.sources.remove(id));
export const useLogNdtSource = () =>
  useNdtWrite<{ id: string; body: NdtSourceLogCreate }, NdtSourceLog>(({ id, body }) => api.ndtInspection.sources.log(id, body));

export const useCreateNdtDose = () => useNdtWrite<NdtDoseWrite, NdtDose>((body) => api.ndtInspection.dosimetry.create(body));
export const useUpdateNdtDose = () =>
  useNdtWrite<{ id: string; body: NdtDoseWrite }, NdtDose>(({ id, body }) => api.ndtInspection.dosimetry.update(id, body));
export const useDeleteNdtDose = () => useNdtWrite<string, unknown>((id) => api.ndtInspection.dosimetry.remove(id));

export interface NdtNewInstrument {
  /** An existing unit to attach to, or a unit created first in this same Save. */
  equipmentId?: string | null;
  /** Present only when a new equipment row is to be made. Equipment create bodies are camelCase. */
  equipment?: { name: string; type: string; serialNumber?: string | null; manufacturer?: string | null; model?: string | null };
  specs: { instrument_kind: string; calibration_interval_days?: number | null; notes?: string | null };
}

/**
 * Step two failed after step one succeeded. The equipment row EXISTS; the
 * caller keeps `equipmentId` and retries step two with it, rather than making
 * a duplicate unit.
 */
export class NdtSpecsAttachError extends Error {
  constructor(public readonly equipmentId: string, public readonly attachError: unknown) {
    super('The equipment row was created, but its NDT specs were not saved.');
    this.name = 'NdtSpecsAttachError';
  }
}

/**
 * Add instrument — the API's two-step contract. With an existing unit, one
 * call (attach). With a new one, POST /equipment and then attach the new id.
 */
export function useAddNdtInstrument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: NdtNewInstrument) => {
      let equipmentId = vars.equipmentId ?? null;
      let created = false;
      if (!equipmentId) {
        if (!vars.equipment) throw new Error('Pick a unit or describe a new one.');
        const res = await api.equipment.create({ ...vars.equipment, status: 'available' });
        const body = res.data as { data?: { id?: unknown } } | null;
        const id = body && typeof body === 'object' && 'data' in body ? body.data?.id : undefined;
        if (typeof id !== 'string' || !id) throw new Error('The equipment create answered without an id.');
        equipmentId = id;
        created = true;
        qc.invalidateQueries({ queryKey: ['equipment'] });
      }
      try {
        const res = await api.ndtInspection.instruments.attach({ equipment_id: equipmentId, ...vars.specs });
        return unwrapNdt<NdtInstrument>(res.data);
      } catch (e) {
        if (created) throw new NdtSpecsAttachError(equipmentId, e);
        throw e;
      }
    },
    onSuccess: () => invalidateNdt(qc),
  });
}

/** The document types a cert upload accepts. */
const DOC_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif']);

/**
 * A cert's document, in four steps that are the API's contract: presign with
 * entityType 'ndt_cert' and entityId = the cert, PUT to R2 (throws unless
 * storage answered 2xx), confirm, then PATCH document_upload_id (the id presign minted; the API accepts it
 * only once confirm has recorded a size). Online only:
 * the offline upload queue has no step four. Every step throws, so nothing
 * reads as attached unless the PATCH answered 200.
 */
export function useAttachNdtCertDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ certId, file }: { certId: string; file: File }) => {
      const contentType = file.type || 'application/pdf';
      if (!DOC_TYPES.has(contentType.toLowerCase())) throw new Error('A certificate document must be a PDF or an image.');
      const presign = await api.uploads.getPresignedUrl({
        fileName: file.name || 'certificate.pdf',
        contentType,
        entityType: 'ndt_cert',
        entityId: certId,
      });
      // The uploads row is minted at presign (`uploadId`); confirm answers only the size.
      const p = presign.data as { uploadUrl?: unknown; key?: unknown; uploadId?: unknown };
      if (typeof p?.uploadUrl !== 'string' || typeof p?.key !== 'string' || typeof p?.uploadId !== 'string') {
        throw new Error('The upload could not be prepared. Try again.');
      }
      const uploadId = p.uploadId;
      await putToPresignedUrl(p.uploadUrl, file, contentType);
      await api.uploads.confirm({ key: p.key, size: file.size });
      return unwrapNdt<NdtTechnicianCert>((await api.ndtInspection.certs.update(certId, { document_upload_id: uploadId })).data);
    },
    onSuccess: () => invalidateNdt(qc),
  });
}


// ═══ Batch 2: jobs, crew, welds, exams (fieldiq-api#1144) ════════════════════

export function useNdtJobs(params?: { status?: string; customerId?: string; q?: string }) {
  return useQuery({
    queryKey: ndtKeys.jobs(params),
    queryFn: async () => unwrapNdtList<NdtJob>((await api.ndtInspection.jobs.list(clean(params))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtJob(id: string | undefined) {
  return useQuery({
    queryKey: ndtKeys.job(id ?? ''),
    queryFn: async () => unwrapNdt<NdtJobDetail>((await api.ndtInspection.jobs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtJobWelds(id: string | undefined) {
  return useQuery({
    queryKey: ndtKeys.jobWelds(id ?? ''),
    queryFn: async () => unwrapNdtList<NdtWeld>((await api.ndtInspection.jobs.welds(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtWelds(params?: { jobId?: string; welderId?: string; standing?: string; q?: string }) {
  return useQuery({
    queryKey: ndtKeys.welds(params),
    queryFn: async () => unwrapNdtList<NdtWeld>((await api.ndtInspection.welds.list(clean(params))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtWeld(id: string | undefined) {
  return useQuery({
    queryKey: ndtKeys.weld(id ?? ''),
    queryFn: async () => unwrapNdt<NdtWeldDetail>((await api.ndtInspection.welds.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtRepairRates(params?: { jobId?: string; welderId?: string }, enabled = true) {
  return useQuery({
    queryKey: ndtKeys.repairRates(params),
    queryFn: async () => unwrapNdt<NdtRepairRates>((await api.ndtInspection.repairRates(clean(params))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export interface NdtCustomerOption {
  id: string;
  name: string;
}

/** The company's customers, for the job form's picker (GET /customers — the existing API). */
export function useNdtCustomers(enabled = true) {
  return useQuery({
    queryKey: ndtKeys.customers(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; companyName?: string | null }>((await api.customers.list()).data)
        .map((c) => ({ id: c.id, name: c.name || c.companyName || '—' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** An exam write's answer: the exam, and the shot guard's warnings (never refusals). */
export interface NdtExamSaved<E> {
  data: E | null;
  meta: NdtMeta;
  warnings: NdtShotWarning[];
}

const warningsOf = (body: unknown): NdtShotWarning[] => {
  const w = body && typeof body === 'object' ? (body as { warnings?: unknown }).warnings : undefined;
  return Array.isArray(w) ? (w as NdtShotWarning[]) : [];
};

export const useCreateNdtJob = () => useNdtWrite<NdtJobWrite, NdtJob>((body) => api.ndtInspection.jobs.create(body));
export const useUpdateNdtJob = () =>
  useNdtWrite<{ id: string; body: NdtJobWrite }, NdtJob>(({ id, body }) => api.ndtInspection.jobs.update(id, body));
export const useDeleteNdtJob = () => useNdtWrite<string, unknown>((id) => api.ndtInspection.jobs.remove(id));
export const useAddNdtCrew = () =>
  useNdtWrite<{ jobId: string; body: NdtCrewWrite }, NdtCrew>(({ jobId, body }) => api.ndtInspection.jobs.addCrew(jobId, body));
export const useRemoveNdtCrew = () => useNdtWrite<string, unknown>((id) => api.ndtInspection.crew.remove(id));
export const useAddNdtWeld = () =>
  useNdtWrite<{ jobId: string; body: NdtWeldWrite }, NdtWeld>(({ jobId, body }) => api.ndtInspection.jobs.addWeld(jobId, body));
export const useUpdateNdtWeld = () =>
  useNdtWrite<{ id: string; body: NdtWeldWrite }, NdtWeld>(({ id, body }) => api.ndtInspection.welds.update(id, body));
export const useDeleteNdtWeld = () => useNdtWrite<string, unknown>((id) => api.ndtInspection.welds.remove(id));
export const useDeleteNdtExam = () => useNdtWrite<string, unknown>((id) => api.ndtInspection.exams.remove(id));

export function useAddNdtExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ weldId, body }: { weldId: string; body: NdtExamWrite }): Promise<NdtExamSaved<NdtExam>> => {
      const res = await api.ndtInspection.welds.addExam(weldId, body);
      return { ...unwrapNdt<NdtExam>(res.data), warnings: warningsOf(res.data) };
    },
    onSuccess: () => invalidateNdt(qc),
  });
}

export function useUpdateNdtExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: NdtExamWrite }): Promise<NdtExamSaved<NdtExam>> => {
      const res = await api.ndtInspection.exams.update(id, body);
      return { ...unwrapNdt<NdtExam>(res.data), warnings: warningsOf(res.data) };
    },
    onSuccess: () => invalidateNdt(qc),
  });
}

// ─── The field surface — snake_case, meta included ───────────────────────────

const EMPTY_FIELD_META: NdtFieldMeta = { unit_system: null, units: null, time_zone: null, today: null };

/** Split a FIELD body. Its meta is snake_cased (`unit_system`, `time_zone`); a missing one is all-null. */
export function unwrapNdtField<T>(body: unknown): { data: T | null; meta: NdtFieldMeta } {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { data: null, meta: EMPTY_FIELD_META };
  const b = body as { data?: unknown; meta?: Partial<NdtFieldMeta> };
  const data = b.data !== undefined && b.data !== null ? (b.data as T) : null;
  return { data, meta: { ...EMPTY_FIELD_META, ...(b.meta ?? {}) } };
}

export function useNdtFieldJobs() {
  return useQuery({
    queryKey: ndtKeys.fieldJobs(),
    queryFn: async () => {
      const { data, meta } = unwrapNdtField<NdtFieldJob[]>((await api.ndtInspection.field.myJobs()).data);
      return { data: Array.isArray(data) ? data : [], meta };
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtFieldJob(id: string | undefined) {
  return useQuery({
    queryKey: ndtKeys.fieldJob(id ?? ''),
    queryFn: async () => unwrapNdtField<NdtFieldJobDetail>((await api.ndtInspection.field.myJob(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useAddNdtFieldWeld() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, body }: { jobId: string; body: NdtWeldWrite }) =>
      unwrapNdtField<NdtFieldWeld>((await api.ndtInspection.field.addWeld(jobId, body)).data),
    onSuccess: () => invalidateNdt(qc),
  });
}

export function useAddNdtFieldExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: NdtExamWrite & { weld_id: string }) => {
      const res = await api.ndtInspection.field.addExam(body);
      return { ...unwrapNdtField<NdtFieldExam>(res.data), warnings: warningsOf(res.data) };
    },
    onSuccess: () => invalidateNdt(qc),
  });
}

// ═══ Batch 3: inspection reports (fieldiq-api#1152) ══════════════════════════

export function useNdtReports(params?: { jobId?: string; status?: string; kind?: string; q?: string }, enabled = true) {
  return useQuery({
    queryKey: ndtKeys.reports(params),
    queryFn: async () => unwrapNdtList<NdtReport>((await api.ndtInspection.reports.list(clean(params))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * One report. `pollMs` refetches on an interval while set — the AI summary is
 * drafted by a background worker after create / regenerate, so the screen
 * polls until the draft lands (or it gives up) rather than guessing.
 */
export function useNdtReport(id: string | undefined, pollMs: number | false = false) {
  return useQuery({
    queryKey: ndtKeys.report(id ?? ''),
    queryFn: async () => unwrapNdt<NdtReport>((await api.ndtInspection.reports.get(id as string)).data),
    enabled: !!id,
    refetchInterval: pollMs,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useReportMutation<V>(fn: (v: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: V) => {
      const res = await fn(v);
      const body = res.data as { warnings?: unknown };
      const warnings = Array.isArray(body?.warnings) ? (body.warnings as NdtReportWarning[]) : [];
      return { ...unwrapNdt<NdtReport>(res.data), warnings };
    },
    onSuccess: () => invalidateNdt(qc),
  });
}

export const useCreateNdtReport = () => useReportMutation((body: NdtReportCreate) => api.ndtInspection.reports.create(body));
export const useUpdateNdtReport = () =>
  useReportMutation(({ id, body }: { id: string; body: NdtReportUpdate }) => api.ndtInspection.reports.update(id, body));
/** Sends `summary` only when the person edited the draft; otherwise the API copies the draft as is. */
export const useApproveNdtReportSummary = () =>
  useReportMutation(({ id, summary }: { id: string; summary?: string }) =>
    api.ndtInspection.reports.approveSummary(id, summary === undefined ? {} : { summary }),
  );
export const useRegenerateNdtReportSummary = () => useReportMutation((id: string) => api.ndtInspection.reports.regenerateSummary(id));
export const useIssueNdtReport = () => useReportMutation((id: string) => api.ndtInspection.reports.issue(id));
export const useVoidNdtReport = () =>
  useReportMutation(({ id, reason }: { id: string; reason: string }) => api.ndtInspection.reports.void(id, { reason }));
